import React, { useState, useEffect, useCallback } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Alert,
  CircularProgress,
  TextField,
  FormControlLabel,
  Checkbox,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { AddCircleOutline as AddIcon } from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs from 'dayjs';
import SingleSelect from '../formItems/SingleSelect';
import unifiedApi from '../../utils/unifiedApi';

const GAME_TYPE_OPTIONS = [
  { id: 'Regular', name: 'Regular' },
  { id: 'Playoff', name: 'Playoff' },
  { id: 'Friendly', name: 'Friendly' },
  { id: 'SemiFinal', name: 'Semi-Final' },
  { id: 'Final', name: 'Final' },
  { id: 'Bye', name: 'BYE' },
];

const getGameTypeLabel = (value) =>
  GAME_TYPE_OPTIONS.find((option) => option.id === value)?.name ?? value;

// Default start: next Thursday at 8:30pm; end one hour later.
const getNextThursday = () => {
  const today = dayjs();
  const daysUntilThursday = (4 - today.day() + 7) % 7 || 7;
  return today.add(daysUntilThursday, 'day');
};

const AddGameDialog = ({ leagueId, open, onClose, onSuccess }) => {
  const buildDefaultStart = () => getNextThursday().hour(20).minute(30).second(0).millisecond(0);

  const [start, setStart] = useState(buildDefaultStart());
  const [end, setEnd] = useState(buildDefaultStart().add(1, 'hour'));
  const [description, setDescription] = useState('Game');
  const [homeTeamId, setHomeTeamId] = useState('');
  const [awayTeamId, setAwayTeamId] = useState('');
  const [venueId, setVenueId] = useState('');
  const [fieldId, setFieldId] = useState('');
  const [gameType, setGameType] = useState('Regular');
  const [isPublished, setIsPublished] = useState(true);

  const [teams, setTeams] = useState([]);
  const [venues, setVenues] = useState([]);
  const [fields, setFields] = useState([]);

  const [dateError, setDateError] = useState('');
  const [formError, setFormError] = useState('');
  const [apiError, setApiError] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);

  const isByeGame = gameType === 'Bye';

  const resetForm = useCallback(() => {
    const defaultStart = buildDefaultStart();
    setStart(defaultStart);
    setEnd(defaultStart.add(1, 'hour'));
    setDescription('Game');
    setHomeTeamId('');
    setAwayTeamId('');
    setVenueId('');
    setFieldId('');
    setGameType('Regular');
    setIsPublished(true);
    setDateError('');
    setFormError('');
    setApiError('');
    setConfirming(false);
    setSaving(false);
  }, []);

  // Load teams and venues when the dialog opens.
  useEffect(() => {
    if (!open) return;

    if (leagueId) {
      unifiedApi
        .get(`/api/teams?leagueId=${leagueId}`)
        .then((data) => setTeams(data || []))
        .catch(() => setTeams([]));
    }

    unifiedApi
      .getVenues()
      .then((data) => setVenues(data || []))
      .catch(() => setVenues([]));
  }, [open, leagueId]);

  // Load fields whenever the selected venue changes.
  useEffect(() => {
    setFields([]);
    if (venueId) {
      unifiedApi
        .get(`/api/Fields?venueId=${venueId}`)
        .then((data) => setFields(data || []))
        .catch(() => setFields([]));
    }
  }, [venueId]);

  // BYE games do not have an away team, venue, or field.
  useEffect(() => {
    if (isByeGame) {
      setAwayTeamId('');
      setVenueId('');
      setFieldId('');
    }
  }, [isByeGame]);

  // Validate that end is after start.
  useEffect(() => {
    if (start && end && (end.isBefore(start) || end.isSame(start))) {
      setDateError('End time must be after start time');
    } else {
      setDateError('');
    }
  }, [start, end]);

  const handleStartChange = (newStart) => {
    if (newStart) {
      setStart(newStart);
      setEnd(newStart.add(1, 'hour'));
    }
  };

  const handleClose = () => {
    if (saving) return;
    resetForm();
    onClose?.();
  };

  const handleReview = (event) => {
    event.preventDefault();
    setFormError('');
    setApiError('');

    if (dateError) return;

    if (!homeTeamId) {
      setFormError('Home team is required.');
      return;
    }

    if (!isByeGame && !awayTeamId) {
      setFormError('Away team is required.');
      return;
    }

    if (gameType !== 'Friendly' && !isByeGame && !fieldId) {
      setFormError('Field is required unless the game type is Friendly or BYE.');
      return;
    }

    setConfirming(true);
  };

  const handleConfirm = async () => {
    setSaving(true);
    setApiError('');

    const selectedAwayTeamId = isByeGame ? null : Number(awayTeamId);
    const selectedVenueId = isByeGame ? null : Number(venueId) || null;
    const selectedFieldId = isByeGame || !fieldId ? null : Number(fieldId);

    const body = {
      // Send the wall-clock time (no timezone suffix) so the backend interprets
      // it as tenant-local time before converting to UTC for storage.
      start: start.format('YYYY-MM-DDTHH:mm:ss'),
      end: end.format('YYYY-MM-DDTHH:mm:ss'),
      description,
      leagueId: Number(leagueId),
      homeTeamId: Number(homeTeamId),
      awayTeamId: selectedAwayTeamId,
      venueId: selectedVenueId,
      fieldId: selectedFieldId,
      gameType,
      isPublished,
    };

    try {
      await unifiedApi.post('/api/Games', body);
      onSuccess?.();
      resetForm();
      onClose?.();
    } catch (error) {
      setApiError(error.message || 'An error occurred while adding the game.');
      setSaving(false);
    }
  };

  const homeTeamName = teams.find((t) => t.id?.toString() === homeTeamId?.toString())?.name ?? '';
  const awayTeamName = teams.find((t) => t.id?.toString() === awayTeamId?.toString())?.name ?? '';
  const venueName = venues.find((v) => v.id?.toString() === venueId?.toString())?.name ?? '';
  const fieldName = fields.find((f) => f.id?.toString() === fieldId?.toString())?.name ?? '';

  const summaryItems = [
    { label: 'Game Type', value: getGameTypeLabel(gameType) },
    { label: 'Date', value: start ? start.format('ddd, MMM D, YYYY') : '' },
    {
      label: 'Time',
      value: start && end ? `${start.format('h:mm a')} – ${end.format('h:mm a')}` : '',
    },
    { label: 'Home Team', value: homeTeamName },
    ...(isByeGame ? [] : [{ label: 'Away Team', value: awayTeamName }]),
    ...(isByeGame ? [] : [{ label: 'Venue', value: venueName || 'Not set' }]),
    ...(isByeGame ? [] : [{ label: 'Field', value: fieldName || 'Not set' }]),
    { label: 'Description', value: description || 'None' },
    { label: 'Published', value: isPublished ? 'Yes' : 'No' },
  ];

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{confirming ? 'Confirm New Game' : 'Add Game'}</DialogTitle>
      <DialogContent>
        {confirming ? (
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary" paragraph>
              Please review the game details before adding it to the schedule.
            </Typography>
            <List dense>
              {summaryItems.map((item) => (
                <ListItem key={item.label} disableGutters>
                  <ListItemText
                    primary={item.value}
                    secondary={item.label}
                  />
                </ListItem>
              ))}
            </List>
            {apiError && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {apiError}
              </Alert>
            )}
          </Box>
        ) : (
          <Box
            component="form"
            id="add-game-form"
            onSubmit={handleReview}
            sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}
          >
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateTimePicker
                label="Start"
                value={start}
                onChange={handleStartChange}
                slotProps={{ textField: { required: true, fullWidth: true } }}
              />
              <DateTimePicker
                label="End"
                value={end}
                onChange={(value) => setEnd(value ?? dayjs())}
                slotProps={{ textField: { required: true, fullWidth: true } }}
              />
            </LocalizationProvider>

            <SingleSelect
              label="Game Type"
              labelId="add-game-type-select-label"
              items={GAME_TYPE_OPTIONS}
              value={gameType}
              onChange={(e) => setGameType(e.target.value)}
            />

            <SingleSelect
              label="Home Team"
              labelId="add-game-home-team-select-label"
              items={teams}
              value={homeTeamId}
              onChange={(e) => {
                setHomeTeamId(e.target.value);
                if (e.target.value === awayTeamId) setAwayTeamId('');
              }}
            />

            {!isByeGame && (
              <>
                <SingleSelect
                  label="Away Team"
                  labelId="add-game-away-team-select-label"
                  items={teams.filter((t) => t.id?.toString() !== homeTeamId?.toString())}
                  value={awayTeamId}
                  onChange={(e) => setAwayTeamId(e.target.value)}
                />

                <SingleSelect
                  label="Venue"
                  labelId="add-game-venue-select-label"
                  items={venues}
                  value={venueId}
                  onChange={(e) => {
                    setVenueId(e.target.value);
                    setFieldId('');
                  }}
                />

                <SingleSelect
                  label="Field"
                  labelId="add-game-field-select-label"
                  items={fields}
                  value={fieldId}
                  onChange={(e) => setFieldId(e.target.value)}
                  disabled={!venueId}
                />
              </>
            )}

            <TextField
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={isPublished}
                  onChange={(e) => setIsPublished(e.target.checked)}
                  color="primary"
                />
              }
              label="Published (visible on public schedule)"
            />

            {dateError && <Alert severity="error">{dateError}</Alert>}
            {formError && <Alert severity="error">{formError}</Alert>}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {confirming ? (
          <>
            <Button onClick={() => setConfirming(false)} disabled={saving}>
              Back
            </Button>
            <Button
              onClick={handleConfirm}
              variant="contained"
              disabled={saving}
              startIcon={saving ? <CircularProgress size={20} /> : <AddIcon />}
            >
              {saving ? 'Adding...' : 'Confirm & Add Game'}
            </Button>
          </>
        ) : (
          <>
            <Button onClick={handleClose}>Cancel</Button>
            <Button
              type="submit"
              form="add-game-form"
              variant="contained"
              disabled={!!dateError}
              startIcon={<AddIcon />}
            >
              Review
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AddGameDialog;
